import type { FormattedAvailability } from 'mobx/Coupons/availability'
import { formatAvailability } from 'mobx/Coupons/availability'
import { AvailabilityState, ChannelAvailability, HoursAvailability, OrderTypeAvailability } from '../availability'
import React, { useCallback, useMemo } from 'react'
import type { Coupon, NonExternalCoupon } from 'shared-types/Coupon'
import type { LanguageLocale } from 'utils/language'
import { getCurrentChannel } from 'mobx/CouponFlow/utils'
import { inject, observer } from 'mobx-react'
import type _Infra from 'mobx/Infra'
import type _User from 'mobx/User'
import { isCouponAvailableAtDate, isCouponExpired, toCouponOrderType } from 'mobx/Coupons/utils'
import type { HoursAvailabilityState } from '../availability/Hour'
import { HoursState } from '../availability/Hour'
import CouponsStore from 'mobx/Coupons'
import { useStores } from 'hooks/useStores'
import { OrderType } from 'mobx/Localization/types'

interface Props {
	coupon: NonExternalCoupon
	locale: LanguageLocale
}

const getHoursAvailabilityState = (coupon: Coupon, expirationMins = 15): HoursAvailabilityState => {
	if (isCouponExpired(coupon)) {
		return HoursState.Expired
	}

	if (!isCouponAvailableAtDate(coupon)) {
		return AvailabilityState.Unavailable
	}

	const future = new Date(new Date().getTime() + expirationMins * 60 * 1000)

	if ((coupon.expiration && coupon.expiration.getTime() < future.getTime()) || !isCouponAvailableAtDate(coupon, future)) {
		return AvailabilityState.Partial
	}

	return AvailabilityState.Available
}

const Availability: React.FC<Props & { User: typeof _User; Infra: typeof _Infra }> = ({ coupon, locale, User, Infra }) => {
	const formattedAvailability: FormattedAvailability = useMemo(() => formatAvailability(coupon.availability!, locale), [coupon, locale])
	const localizedCoupon = useMemo(() => CouponsStore.localizeCoupon(coupon, locale), [coupon, locale])
	const { addressManagerV2Store } = useStores()

	const currentOrderType = useMemo(() => {
		let orderType: string | number | undefined = OrderType.Delivery

		if (
			addressManagerV2Store.newLocalizationFlowEnabled &&
			addressManagerV2Store.alreadyLocalized &&
			addressManagerV2Store.actualFlow?.orderTypeFlow
		) {
			orderType = addressManagerV2Store.actualFlow.orderTypeFlow.orderType
		} else if (!addressManagerV2Store.newLocalizationFlowEnabled) {
			orderType = User!.getOrderType()
		}

		return toCouponOrderType(orderType)
	}, [
		User,
		addressManagerV2Store.actualFlow?.orderTypeFlow,
		addressManagerV2Store.alreadyLocalized,
		addressManagerV2Store.newLocalizationFlowEnabled,
	])

	return (
		<div>
			<HoursAvailability
				weekly={formattedAvailability.weekly}
				exceptions={formattedAvailability.exceptions}
				expiration={coupon.formattedExpiration}
				state={getHoursAvailabilityState(coupon, Infra.appParams.features.expireWarningMins as number | undefined)}
			/>
			<OrderTypeAvailability orderTypes={coupon.orderTypes} currentOrderType={currentOrderType} />
			<ChannelAvailability coupon={localizedCoupon} currentChannel={getCurrentChannel()} redirectText={coupon.code} />
		</div>
	)
}

export default inject('Infra', 'User')(observer(Availability)) as React.FC<Props>
